import { useCallback } from "react";
import { publish } from "../../common/PubSub";
import { IS_USA_MARKET } from "../../utils/EmpUtil";
import { applyBrandChange } from "../pure/changeBrand";

/** Same class instance after applyBrandChange — React will skip re-render; clone so state updates. */
function cloneDealerInstance(instance) {
  if (!instance) return instance;
  return Object.assign(Object.create(Object.getPrototypeOf(instance)), instance);
}

/**
 * Hook that returns a changeBrand(brandCode) function for dealer apps.
 * Uses shared applyBrandChange logic, then runs publish, i18n.changeLanguage, and setUser.
 *
 * @param {Object} params
 * @param {{ current: import("../pure/NADealer").default }} params.userRef - ref holding current user
 * @param {function(import("../pure/NADealer").default | function(prev): import("../pure/NADealer").default): void} params.setUser - state setter for user
 * @param {{ changeLanguage: function(string): void } | undefined} params.i18n - i18n instance for language updates
 * @returns {function(string): void} changeBrand(brandCode)
 */
export function useChangeBrand({ userRef, setUser, i18n }) {
  return useCallback(
    (brandCode) => {
      const user = userRef?.current;
      if (!user) return;

      const isUsaMarket = IS_USA_MARKET(user.market);
      const { updatedUser, language } = applyBrandChange(user, brandCode, {
        isUsaMarket,
        includeFordHubMdlr: true,
        includeLanguage: true,
      });

      const nextUser = cloneDealerInstance(updatedUser);
      setUser(nextUser);

      if (language && i18n?.changeLanguage) {
        i18n.changeLanguage(language);
        nextUser.setLanguageCode(language);
      }

      publish("brandChanged", brandCode);
    },
    [userRef, setUser, i18n]
  );
}
